import { Parser, AST, RelationRef, Expression, Ty } from '@cotera/era';
import { z } from 'zod/v3';
import type { SinkConfig } from './sink-config';
import { SinkConfigSchema } from './sink-config';
import { StreamSpecifierSchema } from './warehouse';

export const ScheduleSchema = z
  .enum(['hourly', 'daily', 'never'])
  .or(z.object({ cron: z.string() }));

export type Schedule = z.infer<typeof ScheduleSchema>;

const ManifestCompileErrorSchema = z.object({
  errorType: z.literal('CompileError'),
  stackTrace: z.string(),
});

export type _Sink = {
  readonly config: SinkConfig;
  readonly condition: AST.ExprFR;
  readonly select: {
    readonly [name: string]: AST.ExprFR;
  };
};

export type Sink = {
  readonly config: SinkConfig;
  readonly condition: (t: RelationRef) => boolean | Expression;
  readonly select: (t: RelationRef) => {
    readonly [attrName: string]: Ty.Scalar | Expression;
  };
};

type SinkType = SinkConfig['t'];

export const SinkSchema: z.ZodSchema<_Sink> = z.object({
  select: z.record(Parser.IdentifierSchema, Parser.ExprSchema),
  condition: Parser.ExprSchema,
  config: SinkConfigSchema,
});

export const ManifestMetaSchema = z.object({
  version: z.string(),
  materializations: z
    .object({
      schema: z.string(),
      viewName: z.string(),
      schedule: z.string().or(z.object({ cron: z.string() })),
    })
    .array(),
  definitions: z.record(
    z.string(),
    z.object({
      id: z.string(),
      displayName: z.string(),
      hidden: z.boolean(),
      attributes: z.record(
        Parser.IdentifierSchema,
        Parser.ExtendedAttributeTypeSchema
      ),
    })
  ),
  schemas: z.object({ read: z.string().array() }),
  eventStreams: z.array(
    z.object({
      specifier: StreamSpecifierSchema,
      sinks: z.record(z.string(), SinkSchema),
      when: ScheduleSchema,
      identifier: Parser.ExprSchema,
      rel: Parser.RelSchema,
    })
  ),
});

export type ManifestMeta = z.infer<typeof ManifestMetaSchema>;
export type ManifestCompilerError = z.infer<typeof ManifestCompileErrorSchema>;

const DevSeverSyncSchema = z.object({
  org: z.object({ id: z.string(), name: z.string() }),
  entities: z.record(
    z.string(),
    z.object({
      idType: Parser.AttributeTypeSchema,
      udds: z.record(
        z.string(),
        z.object({
          ty: Parser.ExtendedAttributeTypeSchema,
          displayName: z.string(),
        })
      ),
    })
  ),
});

type DevSeverSync = z.infer<typeof DevSeverSyncSchema>;
